import { supabase } from '@/integrations/supabase/client';

export interface BriefBulletsNattTask {
  id: string;
  task_type: string;
  schedule_time: string;
  last_run: string | null;
  next_run: string | null;
  status: 'active' | 'paused' | 'failed';
  config: {
    retry_time?: string;
    max_retries?: number;
    notification_email?: string;
    photo_url?: string;
  };
  error_message: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

export interface TaskLog {
  id: string;
  task_id: string;
  task_type: string;
  status: 'success' | 'failed' | 'running';
  message: string | null;
  metadata: any;
  execution_time_ms: number | null;
  created_at: string;
}

export interface BriefBulletsNattArticle {
  id: string;
  title: string;
  created_at: string;
  publication_status: string;
  article_type: string;
}

export class BriefBulletsNattService {
  static async getTaskStatus(): Promise<BriefBulletsNattTask | null> {
    const { data, error } = await supabase
      .from('scheduled_tasks')
      .select('*')
      .eq('task_type', 'briefr_bullets_natt')
      .single();

    if (error) {
      console.error('Error fetching task status:', error);
      return null;
    }

    return data as BriefBulletsNattTask;
  }

  static async getTodayStatus(): Promise<{
    published: boolean;
    article?: BriefBulletsNattArticle;
  }> {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const { data, error } = await supabase
      .from('interviews')
      .select('id, title, created_at, publication_status, article_type')
      .eq('article_type', 'brief_bullets')
      .eq('title', 'Briefr Bullets: Hva skjedde i natt?')
      .gte('created_at', todayStart.toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error checking today status:', error);
      return { published: false };
    }

    return {
      published: !!data,
      article: data ? (data as BriefBulletsNattArticle) : undefined,
    };
  }

  static async triggerManualPublish(
    ntbId?: string,
    searchDate?: string
  ): Promise<{
    success: boolean;
    articleId?: string;
    error?: string;
  }> {
    try {
      const body: any = {};
      
      if (ntbId) {
        body.ntbId = ntbId;
      }
      
      if (searchDate) {
        body.searchDate = searchDate;
      }

      const { data, error } = await supabase.functions.invoke('publish-briefr-bullets-natt', {
        body,
      });

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      return {
        success: data.success,
        articleId: data.articleId,
        error: data.error,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  static async getPublishHistory(days: number = 7): Promise<BriefBulletsNattArticle[]> {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    startDate.setHours(0, 0, 0, 0);

    const { data, error } = await supabase
      .from('interviews')
      .select('id, title, created_at, publication_status, article_type')
      .eq('article_type', 'brief_bullets')
      .eq('title', 'Briefr Bullets: Hva skjedde i natt?')
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching history:', error);
      return [];
    }

    return data as BriefBulletsNattArticle[];
  }

  static async getTaskLogs(limit: number = 10): Promise<TaskLog[]> {
    const { data, error } = await supabase
      .from('task_logs')
      .select('*')
      .eq('task_type', 'briefr_bullets_natt')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching task logs:', error);
      return [];
    }

    return data as TaskLog[];
  }

  static async updateConfig(config: {
    enabled?: boolean;
    schedule_time?: string;
    retry_time?: string;
  }): Promise<boolean> {
    try {
      const updates: any = {};

      if (config.enabled !== undefined) {
        updates.status = config.enabled ? 'active' : 'paused';
      }

      if (config.schedule_time) {
        updates.schedule_time = config.schedule_time;
      }

      if (config.retry_time) {
        updates.config = {
          retry_time: config.retry_time,
          max_retries: 1,
          notification_email: 'admin@briefr.no',
        };
      }

      const { error } = await supabase
        .from('scheduled_tasks')
        .update(updates)
        .eq('task_type', 'briefr_bullets_natt');

      if (error) {
        console.error('Error updating config:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating config:', error);
      return false;
    }
  }

  static async resetRetryCount(): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('scheduled_tasks')
        .update({
          retry_count: 0,
          error_message: null,
          status: 'active',
        })
        .eq('task_type', 'briefr_bullets_natt');

      return !error;
    } catch (error) {
      console.error('Error resetting retry count:', error);
      return false;
    }
  }

  static async toggleAutomaticPublishing(enable: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('scheduled_tasks')
        .update({
          status: enable ? 'active' : 'paused',
        })
        .eq('task_type', 'briefr_bullets_natt');

      if (error) {
        console.error('Error toggling automatic publishing:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error toggling automatic publishing:', error);
      return false;
    }
  }

  static async deleteTodayArticle(): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const todayStatus = await this.getTodayStatus();
      
      if (!todayStatus.published || !todayStatus.article) {
        return {
          success: false,
          error: 'Ingen artikkel funnet for i dag',
        };
      }

      const { error: updateError } = await supabase
        .from('interviews')
        .update({
          is_in_feeder: false,
          publication_status: 'pending',
        })
        .eq('id', todayStatus.article.id);

      if (updateError) {
        return {
          success: false,
          error: updateError.message,
        };
      }

      const { error: deleteError } = await supabase
        .from('interviews')
        .delete()
        .eq('id', todayStatus.article.id);

      if (deleteError) {
        return {
          success: false,
          error: deleteError.message,
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}
